import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Monitor, 
  Smartphone, 
  Tablet, 
  MapPin, 
  Clock, 
  LogOut,
  AlertCircle,
  Shield
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const ActiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/auth/sessions');
      setSessions(response.data.sessions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load active sessions',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleLogoutDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to logout this device?')) {
      return;
    }

    setLogoutLoading(deviceId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/auth/sessions/${deviceId}`);
      toast({
        title: 'Success',
        description: 'Device logged out successfully',
      });
      fetchSessions();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to logout device',
        variant: 'destructive',
      });
    } finally {
      setLogoutLoading(null);
    }
  };

  const handleLogoutAllDevices = async () => {
    if (!window.confirm('Logout all devices? You will need to login again on all devices.')) {
      return;
    }

    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/auth/sessions/logout-all');
      toast({
        title: 'Success',
        description: 'All devices logged out successfully',
      });
      // Redirect to login
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to logout all devices',
        variant: 'destructive',
      });
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="h-6 w-6 text-blue-600" />;
      case 'tablet':
        return <Tablet className="h-6 w-6 text-purple-600" />;
      default:
        return <Monitor className="h-6 w-6 text-green-600" />;
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Active Sessions</h1>
          <p className="text-gray-600 mt-1">Manage devices that are logged into your account</p>
        </div>
        <Button
          onClick={handleLogoutAllDevices}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout All Devices
        </Button>
      </div>

      {/* Security Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">Security Feature</p>
            <p className="text-sm text-blue-700 mt-1">
              View and manage all devices logged into your Tekstar account. If you see an unrecognized device, logout immediately and change your password.
            </p>
          </div>
        </div>
      </Card>

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <Card className="p-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Sessions</h3>
          <p className="text-gray-600">You don't have any active sessions</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sessions.map((session) => (
            <Card key={session.deviceId} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex gap-4 flex-1">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    {getDeviceIcon(session.deviceType)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {session.browser} on {session.os}
                      </h3>
                      <Badge className="bg-green-100 text-green-800">
                        {session.deviceType}
                      </Badge>
                      {session.role && (
                        <Badge className="bg-blue-100 text-blue-800">
                          {session.role}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>IP: {session.ipAddress}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>
                          Last active: {getTimeAgo(session.lastActivity)}
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        Logged in: {formatDateTime(session.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => handleLogoutDevice(session.deviceId)}
                  disabled={logoutLoading === session.deviceId}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  {logoutLoading === session.deviceId ? (
                    'Logging out...'
                  ) : (
                    <>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Session Info */}
      <Card className="p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-2">Session Duration by Role</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700">Riders</p>
            <p className="text-gray-600">30 days</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Drivers/Bikers</p>
            <p className="text-gray-600">14 days</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Admins</p>
            <p className="text-gray-600">2 hours</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ActiveSessions;
